import { memo } from 'react'
import { X } from 'lucide-react'
import { expiryCode } from '@apna/shared'
import { cn } from '@/lib/utils'
import { DigitInput } from '@/components/DigitInput'
import { useTickCell } from '@/feed/useTick'
import type { BasketLegDto } from '@/types/basket'

/**
 * One leg of the basket — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT READS LIKE A POSITION, BECAUSE IT IS ABOUT TO BE ONE
 *
 * Side chip, contract, size, price — the same order and the same vocabulary
 * the Positions drawer uses for a leg already on. A trader comparing what
 * they are about to put on against what they already hold should not have to
 * translate between two layouts, and the basket's whole job is to be read
 * next to the book.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE EXPIRY IS ON THE ROW, NOT ON THE PANEL
 *
 * A leg owns its expiry — the same decision 9.43 made for the strategy ticket
 * — because a basket may hold two dates and a heading naming one would
 * contradict it. It is shown as a code ("27 NOV") rather than an ISO string,
 * and only where there is something to tell apart: see `showExpiry`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE STEPPER IS THE HEADER'S, EXACTLY
 *
 * Same `DigitInput`, same clamping, same "an empty box leaves the last real
 * size alone" rule — the component moved up to `src/components` rather than
 * being reimplemented here, because "the same validations as the current one"
 * is a requirement that there be ONE of it.
 *
 * Zero removes the leg. A zero-lot row cannot be placed, and one sitting in a
 * basket of rows that can is a row that gets counted.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** The ceiling the header's box uses. A paste guard, not a risk limit. */
const MAX_LOTS_TYPED = 10_000_000

export interface BasketLegRowProps {
  leg: BasketLegDto
  /** Draw the expiry: only when the basket holds more than one. */
  showExpiry: boolean
  onLots: (token: number, lots: number) => void
  onRemove: (token: number) => void
}

function BasketLegRowInner({ leg, showExpiry, onLots, onRemove }: BasketLegRowProps) {
  /*
   * The LAST TRADE, not the touch. The panel's net premium is priced at the
   * side the order crosses and says so; this is the row's "what is it worth
   * now", which is the number the ladder shows for the same contract. Two
   * different prices under one label is what makes a reader distrust both.
   */
  const { value: ltp } = useTickCell(leg.token, 'ltp')
  const buy = leg.side === 'BUY'

  return (
    <div
      className={cn('bb-leg', showExpiry && 'dated')}
      data-testid={`basket-leg-${leg.token}`}
    >
      <span className={cn('bb-leg-side', buy ? 'buy' : 'sell')}>{buy ? 'B' : 'S'}</span>

      {/*
        THE EXPIRY IS ITS OWN COLUMN, not a chip inside the contract. Sharing
        one cell let "09NOV" and "1210.05" run together on a two-expiry
        basket — the two most important things to tell apart on that row,
        printed as one string.
      */}
      <span className="bb-leg-name num">
        {leg.strike}
        <span className="ml-1 text-muted-foreground">{leg.optionType}</span>
      </span>

      {showExpiry && (
        <span className="bb-leg-exp num">{expiryCode(leg.expiry) ?? leg.expiry}</span>
      )}

      <span className={cn('bb-leg-ltp num', ltp <= 0 && 'cell-stale')}>
        {ltp > 0 ? ltp.toFixed(2) : '—'}
      </span>

      <span className="tr-stepper bb-leg-size">
        <button
          type="button"
          aria-label={`One lot fewer of ${leg.strike} ${leg.optionType}`}
          onClick={() => onLots(leg.token, leg.lots - 1)}
        >
          −
        </button>
        <DigitInput
          value={leg.lots}
          onValue={(v) => onLots(leg.token, v)}
          max={MAX_LOTS_TYPED}
          /*
           * `null`, as the header's box is: an empty field mid-edit must
           * leave the leg on its last real size rather than deleting it.
           * Zero removes the leg only when it is TYPED or stepped to.
           */
          emptyValue={null}
          label={`Lots of ${leg.strike} ${leg.optionType}`}
          testId={`basket-lots-${leg.token}`}
          placeholder="1"
          className="val num w-[44px] border-0 bg-transparent text-center outline-none"
        />
        <button
          type="button"
          aria-label={`One lot more of ${leg.strike} ${leg.optionType}`}
          onClick={() => onLots(leg.token, leg.lots + 1)}
        >
          +
        </button>
      </span>

      <span className="bb-leg-qty num">{leg.lots * leg.lotSize}</span>

      <button
        type="button"
        className="bb-leg-x"
        aria-label={`Remove ${leg.strike} ${leg.optionType}`}
        data-testid={`basket-remove-${leg.token}`}
        onClick={() => onRemove(leg.token)}
      >
        <X size={13} />
      </button>
    </div>
  )
}

export const BasketLegRow = memo(BasketLegRowInner)
